import {
	AssignmentDraft,
	AssignmentSetupType,
	DEFAULT_DRAFT,
	getFurthestAllowedStep
} from 'LearningManager/constants/createAssignment.constant';

export const ASSIGNMENT_DRAFT_KEY = 'createAssignment:draft';

// Bumped on an incompatible `formData` change so older drafts are dropped.
const DRAFT_VERSION = 1;

// Drafts older than this are treated as abandoned rather than resumable.
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const SETUP_TYPES: AssignmentSetupType[] = ['Quick Setup', 'Custom Setup'];

export interface PersistedAssignmentDraft {
	version: number;
	// A draft is never restored into a session on a different tenant.
	tenantId: string;
	// Lets a super admin's reload put the tenant back in the sidebar selector.
	tenantName?: string;
	// Which step list `currentStep` indexes into.
	flowType: AssignmentSetupType;
	currentStep: number;
	formData: AssignmentDraft;
	savedAt: number;
}

const isSetupType = (value: unknown): value is AssignmentSetupType =>
	SETUP_TYPES.includes(value as AssignmentSetupType);

// Storage is best-effort — a draft is never worth breaking the page over.
const safeGetItem = (key: string): string | null => {
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
};

export const clearAssignmentDraft = (): void => {
	try {
		window.localStorage.removeItem(ASSIGNMENT_DRAFT_KEY);
	} catch {
		// nothing to do — the draft is already unreachable
	}
};

export const writeAssignmentDraft = (
	draft: Omit<PersistedAssignmentDraft, 'version' | 'savedAt'>
): void => {
	try {
		const payload: PersistedAssignmentDraft = {
			...draft,
			version: DRAFT_VERSION,
			savedAt: Date.now()
		};
		window.localStorage.setItem(
			ASSIGNMENT_DRAFT_KEY,
			JSON.stringify(payload)
		);
	} catch {
		// quota exceeded or storage disabled — carry on without a draft
	}
};

export interface ReadAssignmentDraftOptions {
	// The tenant this session is scoped to, or `null` for a super admin with no
	// tenant selected in the sidebar (the draft's tenant is taken).
	currentTenantId: string | null;
	// The setup type already in the URL, which wins over the stored one.
	expectedFlowType?: AssignmentSetupType | null;
}

// Reads the stored draft, discarding it if malformed, outdated, stale or from
// another tenant.
export const readAssignmentDraft = ({
	currentTenantId,
	expectedFlowType
}: ReadAssignmentDraftOptions): PersistedAssignmentDraft | null => {
	const raw = safeGetItem(ASSIGNMENT_DRAFT_KEY);
	if (!raw) return null;

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		clearAssignmentDraft();
		return null;
	}

	const draft = parsed as Partial<PersistedAssignmentDraft> | null;

	const isUsable =
		Boolean(draft) &&
		draft?.version === DRAFT_VERSION &&
		isSetupType(draft?.flowType) &&
		typeof draft?.currentStep === 'number' &&
		typeof draft?.savedAt === 'number' &&
		Boolean(draft?.formData) &&
		typeof draft?.tenantId === 'string';

	if (!isUsable) {
		clearAssignmentDraft();
		return null;
	}

	const usable = draft as PersistedAssignmentDraft;

	if (Date.now() - usable.savedAt > DRAFT_MAX_AGE_MS) {
		clearAssignmentDraft();
		return null;
	}

	if (currentTenantId !== null && usable.tenantId !== currentTenantId) {
		clearAssignmentDraft();
		return null;
	}

	if (expectedFlowType && usable.flowType !== expectedFlowType) {
		clearAssignmentDraft();
		return null;
	}

	return {
		...usable,
		// Merged over the defaults so a field added since restores as present.
		formData: {
			configure: {
				...DEFAULT_DRAFT.configure,
				...usable.formData.configure
			},
			quickSetup: {
				...DEFAULT_DRAFT.quickSetup,
				...usable.formData.quickSetup
			},
			customSetup: {
				...DEFAULT_DRAFT.customSetup,
				...usable.formData.customSetup
			}
		}
	};
};

// Clamps a stored step so a draft never reopens past a step missing its data.
export const resolveResumableStep = (
	draft: PersistedAssignmentDraft
): number => {
	const furthest = getFurthestAllowedStep(draft.flowType, draft.formData);
	return Math.max(0, Math.min(draft.currentStep, furthest));
};
